(() => {
  const desktopPointer = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 960px)');
  if (!desktopPointer.matches) return;

  const cursor = document.querySelector('[data-wr-cursor]');
  if (!cursor) return;

  const outline = document.createElement('div');
  outline.className = 'wr-link-venom';
  outline.setAttribute('aria-hidden', 'true');
  document.body.appendChild(outline);

  let activeLink = null;
  let pointerX = innerWidth / 2;
  let pointerY = innerHeight / 2;
  let switchToken = 0;
  let layoutFrame = 0;

  const ctaSelector = [
    'a.wr-button',
    'a.wr-statement__cta',
    'a.wr-mira-brand-card__link',
    '.wr-collections-editorial__copy > a',
    '.wr-mira-footer__legal a'
  ].join(',');

  const excludedAreaSelector = [
    '.wr-mira-topnav',
    '.wr-mira-menu',
    '.wr-about-page',
    '.wr-mira-contact-button',
    '.wr-mira-contains__hotspot',
    '.wr-hotspot'
  ].join(',');

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const textRects = (link) => {
    const rects = [];
    const walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (!parent || parent.closest('[aria-hidden="true"]')) return NodeFilter.FILTER_REJECT;
        const style = getComputedStyle(parent);
        return style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      }
    });

    while (walker.nextNode()) {
      const range = document.createRange();
      range.selectNodeContents(walker.currentNode);
      [...range.getClientRects()].forEach((rect) => {
        if (rect.width > 1 && rect.height > 1 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth) rects.push(rect);
      });
    }
    return rects;
  };

  const closestTextLine = (link) => {
    const rects = textRects(link);
    if (!rects.length) return null;

    const candidates = rects.filter((rect) => pointerY >= rect.top - 8 && pointerY <= rect.bottom + 8);
    const lineRects = candidates.length ? candidates : rects;
    const nearest = lineRects.reduce((best, rect) => {
      const dx = pointerX < rect.left ? rect.left - pointerX : pointerX > rect.right ? pointerX - rect.right : 0;
      const dy = pointerY < rect.top ? rect.top - pointerY : pointerY > rect.bottom ? pointerY - rect.bottom : 0;
      const distance = dx * dx + dy * dy;
      return !best || distance < best.distance ? { rect, distance } : best;
    }, null)?.rect;
    if (!nearest) return null;

    const sameLine = rects.filter((rect) => rect.top < nearest.bottom - 1 && rect.bottom > nearest.top + 1);
    const box = { left: nearest.left, top: nearest.top, right: nearest.right, bottom: nearest.bottom };
    let changed = true;
    while (changed) {
      changed = false;
      sameLine.forEach((rect) => {
        if (rect.right < box.left - 12 || rect.left > box.right + 12) return;
        const next = {
          left: Math.min(box.left, rect.left),
          top: Math.min(box.top, rect.top),
          right: Math.max(box.right, rect.right),
          bottom: Math.max(box.bottom, rect.bottom)
        };
        if (next.left !== box.left || next.top !== box.top || next.right !== box.right || next.bottom !== box.bottom) {
          Object.assign(box, next);
          changed = true;
        }
      });
    }
    return box;
  };

  const eligibleLink = (target) => {
    const link = target?.closest?.(ctaSelector);
    if (!link || link.closest(excludedAreaSelector)) return null;
    if (link === activeLink) return link;
    if (link.closest('[aria-hidden="true"]') || getComputedStyle(link).pointerEvents === 'none') return null;
    return textRects(link).length ? link : null;
  };

  const syncBaseCursor = (target) => {
    if (!target?.closest) return;
    const zoomTarget = target.closest('.wr-product__gallery[data-product-zoom="true"]');
    const productTarget = target.closest('[data-cursor-product]');
    const linkTarget = target.closest('a,button,input,select,label,[role="button"]');
    const clickStage = target.closest('[data-cursor-click]');
    const interactive = target.closest('a,button,input,select,textarea');

    cursor.classList.toggle('is-zoom', Boolean(zoomTarget));
    cursor.classList.toggle('is-product', !zoomTarget && Boolean(productTarget));
    cursor.classList.toggle('is-link', !zoomTarget && !productTarget && Boolean(linkTarget));
    cursor.classList.toggle('is-click', Boolean(clickStage && !interactive));
  };

  const placeOutline = (link, usePointerOrigin = false) => {
    const rect = closestTextLine(link);
    if (!rect) return false;

    const padX = 7;
    const padY = 4;
    const left = Math.round(rect.left - padX);
    const top = Math.round(rect.top - padY);
    const width = Math.max(12, Math.round(rect.right - rect.left + padX * 2));
    const height = Math.max(12, Math.round(rect.bottom - rect.top + padY * 2));
    const originX = clamp(pointerX - left, 0, width);
    const originY = clamp(pointerY - top, 0, height);
    const spread = Math.ceil(Math.max(
      Math.hypot(originX, originY),
      Math.hypot(width - originX, originY),
      Math.hypot(originX, height - originY),
      Math.hypot(width - originX, height - originY)
    ) + 8);

    outline.style.setProperty('--wr-venom-x', `${left}px`);
    outline.style.setProperty('--wr-venom-y', `${top}px`);
    outline.style.setProperty('--wr-venom-w', `${width}px`);
    outline.style.setProperty('--wr-venom-h', `${height}px`);
    if (usePointerOrigin || !outline.classList.contains('is-active')) {
      outline.style.setProperty('--wr-venom-ox', `${originX}px`);
      outline.style.setProperty('--wr-venom-oy', `${originY}px`);
      outline.style.setProperty('--wr-venom-spread', `${spread}px`);
    }
    return true;
  };

  const enter = (link) => {
    switchToken += 1;
    activeLink?.classList.remove('wr-venom-link-active');
    activeLink = link;
    activeLink.classList.add('wr-venom-link-active');
    outline.classList.add('is-resetting');
    outline.classList.remove('is-active', 'is-splashing');
    if (!placeOutline(link, true)) {
      activeLink.classList.remove('wr-venom-link-active');
      activeLink = null;
      outline.classList.remove('is-resetting');
      return;
    }
    void outline.offsetWidth;
    outline.classList.remove('is-resetting');
    requestAnimationFrame(() => outline.classList.add('is-active', 'is-splashing'));
    cursor.classList.add('is-venom-bound');
    window.setTimeout(() => outline.classList.remove('is-splashing'), 620);
  };

  const leave = () => {
    if (!activeLink) return;
    const token = ++switchToken;
    activeLink.classList.remove('wr-venom-link-active');
    placeOutline(activeLink, true);
    activeLink = null;
    outline.classList.remove('is-active', 'is-splashing');
    window.setTimeout(() => {
      if (token !== switchToken || activeLink) return;
      cursor.classList.remove('is-venom-bound');
    }, 120);
  };

  const syncTarget = (target) => {
    const link = eligibleLink(target);
    if (link === activeLink) return;
    if (link) enter(link);
    else leave();
  };

  const queueViewportSync = () => {
    if (layoutFrame) return;
    layoutFrame = requestAnimationFrame(() => {
      layoutFrame = 0;
      const target = document.elementFromPoint(pointerX, pointerY);
      syncBaseCursor(target);
      syncTarget(target);
      if (activeLink) placeOutline(activeLink, false);
    });
  };

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    syncTarget(event.target);
  }, { passive: true });

  document.addEventListener('pointerleave', leave, { passive: true });
  window.addEventListener('blur', leave);
  window.addEventListener('resize', queueViewportSync, { passive: true });
  window.addEventListener('scroll', queueViewportSync, { passive: true });
  window.addEventListener('wheel', queueViewportSync, { passive: true });
})();
